import {
  useState, useMemo, useEffect
} from 'react';

import {
  useGetGroupQuestionSetsAllQuery,
  useGetOrgQuestionsSetsEditingQuery,
} from 'src/redux/openapi';
import {
  useGetQuestionsByQuestionSetIdAllMutation
} from 'src/shared/api/groups/groupsApi';
import {
  showWarningFromServer
} from 'src/shared/utils';

import {
  PreparedQuestionSets
} from '../types';

interface UseGetQuestionsToEdit {
  groupId?: string | null;
  organizationId?: string;
}

export const useGetQuestionsToEdit = ({
  groupId = '',
  organizationId,
}: UseGetQuestionsToEdit) => {
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);
  const [allSets, setAllSets] = useState<PreparedQuestionSets[]>([]);

  const [getGroupQuestionsBySetIdAll] = useGetQuestionsByQuestionSetIdAllMutation();

  const {
    data: questionSetsData,
    isLoading: isQuestionSetsLoading,
    isFetching: isQuestionSetsFetching,
  } = useGetGroupQuestionSetsAllQuery(
    {
      id: groupId || '',
      // TODO: add pagination after BE or design changing
      limit: 5000,
      filters: {
        sortOrder: 'asc',
        sortBy: 'startDate',
      },
    },
    {
      skip: !groupId,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    questionSets: groupQuestionSets, activatedQuestions
  } = useMemo(
    () => questionSetsData?.data || {
      total: 0,
      questionSets: [],
      activatedQuestions: [],
    },
    [questionSetsData],
  );

  const groupQueryComplete = !!groupId && !isQuestionSetsLoading && !isQuestionSetsFetching;

  const useOrgFallback = !!organizationId
    && (!groupId || (groupQueryComplete && groupQuestionSets.length === 0));

  const {
    data: orgSetsData,
    isLoading: isOrgSetsLoading,
    isFetching: isOrgSetsFetching,
  } = useGetOrgQuestionsSetsEditingQuery(
    {
      id: organizationId || '',
      limit: 5000,
      filters: {
        notStatus: 'DEACTIVATED',
      },
    },
    {
      skip: !useOrgFallback,
      refetchOnMountOrArgChange: true,
    },
  );

  const questionSets: PreparedQuestionSets[] = useMemo(
    () => {
      if (groupQuestionSets.length > 0) {
        return groupQuestionSets;
      }

      const orgSets = orgSetsData?.data.questionSets;

      if (orgSets?.length) {
        return orgSets.map((set) => ({
          id: set.id,
          title: set.title,
          createdAt: set.createdAt,
          updatedAt: set.updatedAt,
          startDate: set.startDate,
          endDate: set.endDate,
          groupStatus: null,
          status: null,
          questionCount: set.questions.length,
          activatedQuestionCount: 0,
          questions: set.questions.map((q) => ({
            id: q.id,
            title: q.title,
            type: q.type,
            status: q.status,
            startDate: q.startDate,
            endDate: q.endDate,
            createdAt: q.createdAt,
            updatedAt: q.updatedAt,
            isMandatory: q.isMandatory,
            isPreferred: q.isPreferred,
          })),
        }));
      }

      return [];
    },
    [groupQuestionSets, orgSetsData]
  );

  useEffect(
    () => {
      if (
        isQuestionsLoading
      && !isQuestionSetsLoading
      && !isQuestionSetsFetching
      && !isOrgSetsLoading
      && !isOrgSetsFetching
      ) {
        setIsQuestionsLoading(false);
      }
    },
    [
      isQuestionsLoading,
      isQuestionSetsLoading,
      isQuestionSetsFetching,
      isOrgSetsLoading,
      isOrgSetsFetching,
    ]
  );

  useEffect(
    () => {
      setAllSets(questionSets);
    },
    [questionSets]
  );

  const onLoadQuestionsBySetId = async (questionSetId: string) => {
    const loadedQuestions = allSets.find(
      (set) => set.id === questionSetId,
    )?.questions;

    if (loadedQuestions) {
      return loadedQuestions;
    }

    if (!groupId) {
      return [];
    }

    try {
      const response = await getGroupQuestionsBySetIdAll({
        id: groupId,
        questionSetId,
        limit: 5000,
      }).unwrap();

      const questions = response?.data?.questions || [];

      setAllSets((prev) => prev.map((set) => {
        return set.id === questionSetId
          ? {
            ...set,
            questions,
          }
          : set;
      }),);

      return questions;
    } catch (error) {
      showWarningFromServer(error);
    }

    return [];
  };

  const isLoading = isQuestionSetsLoading
    || isQuestionsLoading
    || (useOrgFallback && (isOrgSetsLoading || isOrgSetsFetching));

  return {
    preparedSets: allSets,
    activatedQuestions,
    isQuestionsLoading: isLoading,
    setIsQuestionsLoading,
    onLoadQuestionsBySetId,
  };
};
